import { FC, useCallback, useMemo } from "react";

import { EnumOption, EnumSchemaItem } from "@data-root/types";
import { InputWithOptions } from ".";
import { SchemaItemProps, useField } from "@data-root/react";
import { inputGuard } from "../../hocs/guard";
import { View } from "@data-root/react";
import { Button, Skeleton, Space } from "antd";
import { Typography, Tooltip } from "antd";
import { Remarks } from "../remarks";

const { Title } = Typography;

export const EnumInputModalPresentation: FC<SchemaItemProps<EnumSchemaItem>> = ({ item }) => {
  const options = useMemo(() => item.options, [item.options]);

  return <InputWithOptions item={item} options={options} />;
};

export const EnumInputInlinePresentation: FC<SchemaItemProps<EnumSchemaItem>> = ({ item }) => {
  const { loaded, displayName, editable, show, value, setValue: _setValue, remarks } = useField(item);

  const handleSetValue = useCallback(
    (option: EnumOption) => {
      if (!editable || !loaded) return;

      _setValue(option.id, option.name);
    },
    [_setValue, editable, loaded]
  );

  if (!show) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      <Space wrap>
        {item.options.map((option) => (
          <Tooltip key={option.id} title={option.description ?? option.name}>
            <Button
              loading={!loaded}
              key={option.id}
              onClick={() => handleSetValue(option)}
              type={option.id === value ? "primary" : "default"}
            >
              {option.name}
            </Button>
          </Tooltip>
        ))}
      </Space>

      <Remarks texts={remarks} />
    </View>
  );
};

export const EnumInputColorPresentation: FC<SchemaItemProps<EnumSchemaItem>> = ({ item }) => {
  const { loaded, name, editable, show, value, setValue: _setValue, remarks } = useField(item);

  const handleSetValue = useCallback(
    (option: EnumOption) => {
      if (!editable || !loaded) return;

      _setValue(option.id, option.name);
    },
    [_setValue, editable, loaded]
  );

  if (!show) return null;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{name}</Title>

      {!loaded ? (
        <Skeleton.Input block active />
      ) : (
        <Space wrap>
          {item.options.map((option) => (
            <View
              style={{
                width: 30,
                height: 30,
                backgroundColor: option.color!,
                borderRadius: 15,
                borderWidth: 4,
                borderColor: option.id === value ? "black" : "transparent",
              }}
              onClick={() => handleSetValue(option)}
            />
          ))}
        </Space>
      )}

      <Remarks texts={remarks} />
    </View>
  );
};

export const EnumInput: FC<SchemaItemProps<EnumSchemaItem>> = inputGuard((props) => {
  const { item } = props;
  const { presentation: _presentation } = item;

  const presentation = useMemo(() => _presentation || "Modal", [_presentation]);

  switch (presentation) {
    case "Inline":
      return <EnumInputInlinePresentation {...props} />;
    case "Color":
      return <EnumInputColorPresentation {...props} />;
    case "Modal":
    default:
      return <EnumInputModalPresentation {...props} />;
  }
});
